import { Button, Form, Input, InputNumber, Modal, Switch } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addLabel, editLabel } from "../../redux/actions/labelActions";
import { RootState } from "../../redux/rootReducer";
import { Actions, Status } from "../../shared/constant";
import validateMessages, { Max100Validate } from "../../utils/validate";
import UploadImage from "../_shared/UploadImage";

interface ModalFormProps {
  visible: boolean;
  onCancel: () => void;
  callEffect: () => void;
  mode: string;
  editData: any;
}

const LabelModalForm = ({
  visible,
  onCancel,
  callEffect,
  mode,
  editData,
}: ModalFormProps) => {
  const [isActive, setIsActive] = useState(true);
  const [file, setFile] = useState(null);
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { pending, fulfilled } = useSelector((state: RootState) => state.label);

  useEffect(() => {
    // console.log("useEffect editData: ", mode, editData);
    if (mode === Actions.EDIT) {
      if (editData.status === Status.INACTIVE) setIsActive(false);
      form.setFieldsValue({
        labelName: editData.name,
        country: editData.country,
        share_percentage: editData.share_percentage,
        status: editData.status === Status.ACTIVE,
      });
    }
  }, [editData]);

  useEffect(() => {
    /* Create and Edit label success */
    if (mode === Actions.CREATE || mode === Actions.EDIT) {
      if (fulfilled) {
        onCancel();
        callEffect();
      }
    }
  }, [pending]);

  const onFileUpload = (content: any) => {
    setFile(content);
  };

  const onToggleSwitch = (checked: boolean) => {
    // console.log(`switch to ${checked}`);
    setIsActive(checked);
  };

  const onOk = async () => {
    try {
      await form.validateFields();
    } catch (errorInfo) {
      return;
    }

    const formFields = form.getFieldsValue(true);
    const formData = new FormData();
    if (
      (mode === Actions.EDIT &&
        editData.name.trim() !== formFields.labelName.trim()) ||
      mode === Actions.CREATE
    )
      formData.append("name", formFields.labelName);
    formData.append("country", formFields.country);
    formData.append("share_percentage", formFields.share_percentage);
    formData.append(
      "status",
      formFields.status ? Status.ACTIVE : Status.INACTIVE,
    );
    if (file) formData.append("logo", file as unknown as Blob);

    if (mode === Actions.CREATE) dispatch(addLabel(formData));
    if (mode === Actions.EDIT) dispatch(editLabel(editData.id, formData));
  };

  const afterClose = () => {
    form.resetFields();
    setIsActive(true);
    setFile(null);
  };

  return (
    <Modal
      visible={visible}
      title={`${mode === Actions.CREATE ? "Create" : "Edit"} Label`}
      centered
      width={740}
      maskClosable={false}
      onOk={onOk}
      onCancel={onCancel}
      afterClose={afterClose}
      destroyOnClose
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={pending as boolean}
          onClick={onOk}
        >
          Save
        </Button>,
      ]}
    >
      <div className="mx-6 mt-6 mb-[26px]">
        <Form
          form={form}
          layout="vertical"
          name="labelForm"
          validateMessages={validateMessages}
        >
          <Form.Item name="logo" label="Label Logo">
            <UploadImage onFileUpload={onFileUpload} />
          </Form.Item>
          <div className="grid grid-cols-2 gap-x-6">
            <Form.Item
              name="labelName"
              label="Label Name"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
            <Form.Item name="country" label="Country" initialValue="">
              <Input />
            </Form.Item>
            <Form.Item
              name="share_percentage"
              label="Percentage of Income Share"
              rules={[
                { required: true },
                { validator: (_: any, value: number) => Max100Validate(value) },
              ]}
            >
              <InputNumber placeholder="Income Share %" className="w-full" />
            </Form.Item>
          </div>
          <div className="flex gap-[26px] items-center mt-4">
            <div>
              <Form.Item
                valuePropName="checked"
                name="status"
                className="h-4"
                initialValue
              >
                <Switch defaultChecked onChange={onToggleSwitch} />
              </Form.Item>
            </div>
            <div className="h-5">{isActive ? "Active" : "Inactive"}</div>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default LabelModalForm;
